/** Shared sheet-linking helpers for foreman + planning surfaces (D066-LINKER-W1). */

function coerceJobsiteIdValue(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed || null;
  }
  if (typeof raw === 'object') {
    const nested = raw._id ?? raw.id ?? raw.$oid;
    if (nested != null) return coerceJobsiteIdValue(nested);
  }
  const coerced = String(raw);
  return coerced && coerced !== '[object Object]' ? coerced : null;
}

/** Resolve a planning-session jobsite id from API/session shapes (ObjectId, nested jobsite, string). */
export function resolvePlanningSessionJobsiteId(source, fallback = null) {
  if (source == null) return fallback || null;
  const raw = typeof source === 'object' && !Array.isArray(source)
    ? (source.jobsiteId ?? source.jobsite ?? null)
    : source;
  return coerceJobsiteIdValue(raw) || fallback || null;
}

/** Build per-task linkedSheets map from queued or persisted planning manual edits. */
export function collectTaskLinkedSheetsFromEdits(edits = []) {
  const map = {};
  for (const edit of edits) {
    if (edit?.editType !== 'modify_task') continue;
    if (!Array.isArray(edit.payload?.linkedSheets)) continue;
    const taskId = edit.targetTaskId;
    if (!taskId) continue;
    map[String(taskId)] = edit.payload.linkedSheets;
  }
  return map;
}

export function sheetTupleKey({ documentId, page }) {
  const pageKey = page === null ? 'whole-document' : Number(page);
  return `${String(documentId || '')}:${pageKey}`;
}

export function isFallbackSheetLabel(label) {
  if (!label) return true;
  return /^page\s+\d+$/i.test(String(label).trim());
}

export function displayPageNumber(page) {
  const numeric = Number(page);
  if (!Number.isInteger(numeric) || numeric < 0) return '—';
  return `Page ${numeric + 1}`;
}

export function sheetPrimaryLine(sheet) {
  const title = sheet.documentTitle || sheet.title || 'Untitled document';
  const pageLabel = sheet.legacyDocumentOnly
    ? 'Whole document'
    : displayPageNumber(sheet.page ?? sheet.pageIndex);
  return `${title} · ${pageLabel}`;
}

export function sheetSecondaryLine(sheet) {
  const label = sheet.sheetNumber || sheet.code || sheet.sheetNumberSnapshot || sheet.sheetLabel || '';
  if (!label || isFallbackSheetLabel(label)) return '';
  return String(label).trim();
}

export function blueprintInventoryRow(sheet) {
  const page = Number.isInteger(Number(sheet.page))
    ? Number(sheet.page)
    : Number.isInteger(Number(sheet.pageIndex))
      ? Number(sheet.pageIndex)
      : 0;
  const label = sheet.sheetNumber || sheet.code || sheet.title || '';
  const isFallbackLabel = isFallbackSheetLabel(label);
  const documentTitle = sheet.documentTitle
    || (isFallbackLabel ? 'Untitled document' : (sheet.title || 'Untitled document'));
  return {
    documentId: String(sheet.documentId || ''),
    page,
    documentTitle,
    sheetNumber: sheet.sheetNumber || sheet.code || null,
    sheetLabel: sheet.title || sheet.sheetLabel || null,
    category: sheet.category || null,
    isFallbackLabel,
    tupleKey: sheetTupleKey({ documentId: sheet.documentId, page }),
  };
}

export function groupBlueprintRowsByDocument(rows) {
  const groups = new Map();
  for (const row of rows) {
    if (!row.documentId) continue;
    const existing = groups.get(row.documentId);
    if (existing) {
      existing.sheets.push(row);
    } else {
      groups.set(row.documentId, {
        documentId: row.documentId,
        documentTitle: row.documentTitle,
        sheets: [row],
      });
    }
  }
  for (const group of groups.values()) {
    group.sheets.sort((left, right) => left.page - right.page);
  }
  return [...groups.values()].sort((left, right) =>
    left.documentTitle.localeCompare(right.documentTitle)
  );
}

export function planningTaskKey(task, index = 0) {
  const explicit = [task?.id, task?.taskId, task?._id, task?.taskIdentity]
    .map((value) => value?.toString?.() || String(value || ''))
    .find(Boolean);
  if (explicit) return explicit;
  const titleKey = String(task?.title || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return titleKey ? `task:${index}:${titleKey}` : `task:${index}`;
}

export function normalizeLinkedSheetRows({ linkedSheets = [], linkedDocuments = [] } = {}) {
  const docById = new Map(
    (Array.isArray(linkedDocuments) ? linkedDocuments : []).map((doc) => [
      String(doc.documentId || doc._id || doc.id || ''),
      doc,
    ])
  );

  const sheetRows = Array.isArray(linkedSheets) && linkedSheets.length > 0
    ? linkedSheets.map((sheet) => {
      const documentId = String(sheet.documentId || '');
      const page = Number(sheet.page);
      const doc = docById.get(documentId);
      const sheetNumber = sheet.sheetNumberSnapshot || sheet.sheetNumber || null;
      return {
        documentId,
        page,
        tupleKey: sheetTupleKey({ documentId, page }),
        documentTitle: doc?.title || sheet.documentTitle || 'Untitled document',
        sheetNumber,
        category: doc?.documentCategory || sheet.category || null,
      };
    })
    : [];

  const sheetDocumentIds = new Set(sheetRows.map((row) => row.documentId));
  const legacyRows = (Array.isArray(linkedDocuments) ? linkedDocuments : [])
    .map((doc) => {
      const documentId = String(doc.documentId || doc._id || doc.id || '');
      if (!documentId || sheetDocumentIds.has(documentId)) return null;
      return {
        documentId,
        page: null,
        tupleKey: sheetTupleKey({ documentId, page: null }),
        documentTitle: doc.title || 'Untitled document',
        sheetNumber: Array.isArray(doc.sheetNumbers) ? doc.sheetNumbers[0] : null,
        category: doc.documentCategory || null,
        legacyDocumentOnly: true,
      };
    })
    .filter(Boolean);

  if (sheetRows.length > 0) {
    return [...sheetRows, ...legacyRows];
  }

  return legacyRows;
}

export function suggestionLinkSheet(suggestion) {
  if (suggestion?.linkSheet?.documentId != null) {
    return {
      documentId: String(suggestion.linkSheet.documentId),
      page: Number(suggestion.linkSheet.page),
    };
  }
  const page = Number.isInteger(Number(suggestion?.page))
    ? Number(suggestion.page)
    : Number.isInteger(Number(suggestion?.pageIndex))
      ? Number(suggestion.pageIndex)
      : 0;
  return {
    documentId: String(suggestion?.documentId || ''),
    page,
  };
}

export function rejectedSheetTuples(error) {
  const rejected = error?.data?.data?.rejectedSheets;
  return Array.isArray(rejected) ? rejected : [];
}

export function sheetMutationErrorMessage(error) {
  const rejected = rejectedSheetTuples(error);
  if (rejected.length > 0) {
    const formatted = rejected
      .map((sheet) => `${sheet.documentId}:${sheet.page}`)
      .join(', ');
    return `Could not update linked sheets. Rejected sheet tuples: ${formatted}`;
  }
  const legacyRejected = error?.data?.data?.rejectedDocumentIds;
  if (Array.isArray(legacyRejected) && legacyRejected.length > 0) {
    return `Could not update linked sheets. Rejected document ids: ${legacyRejected.join(', ')}`;
  }
  return 'Could not update linked sheets. Try again.';
}
